<template>
  <div class="msg-root">
    <div id="layout-wrapper" class="msg-layout-wrapper">
      <div class="msg-topbar">
        <div class="container d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center gap-2">
            <button class="msg-back" @click="goBack"><i class="mdi mdi-arrow-left"></i></button>
            <a href="javascript:void(0)" @click="$router.push('/')">
              <img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:30px;" />
            </a>
            <span class="msg-title">Chats</span>
          </div>
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="btn btn-sm btn-outline-warning">Dashboard</a>
        </div>
      </div>

      <div class="container msg-container">
        <div class="msg-search">
          <i class="mdi mdi-magnify"></i>
          <input v-model="search" placeholder="Search chats" />
        </div>

        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-warning"></div>
        </div>

        <div v-else-if="filteredConversations.length === 0" class="text-center py-5">
          <i class="mdi mdi-message-text-outline msg-empty-icon"></i>
          <p class="mt-3 msg-empty-text">{{ search ? 'No chats match' : 'No conversations yet' }}</p>
          <p class="msg-empty-subtext">Messages from the community, offers and orders will appear here</p>
        </div>

        <div v-else>
          <div v-for="c in filteredConversations" :key="c.id" class="msg-conv" @click="openChat(c)">
            <div class="msg-avatar-wrap">
              <user-avatar :src="c.other_avatar" :name="c.other_name" size="md" />
              <div v-if="c.unread_count > 0" class="msg-unread-dot"></div>
              <div v-if="c.other_online === '1' || c.other_online === 1" class="msg-online-dot"></div>
            </div>

            <div class="flex-fill" style="min-width:0;">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <span class="msg-name" :class="{ unread: c.unread_count > 0 }">{{ c.other_name || 'User' }}</span>
                <span class="msg-time">{{ timeAgo(c.last_message_at || c.updated_at) }}</span>
              </div>

              <div class="msg-preview" :class="{ unread: c.unread_count > 0 }">
                {{ formatPreview(c.last_message_preview) }}
              </div>

              <!-- Presence pill (WhatsApp-style) -->
              <div class="msg-presence" v-if="isOnline(c) || c.other_last_seen">
                <span v-if="isOnline(c)" class="msg-pres-online"><span class="msg-pres-dot"></span> Online</span>
                <span v-else class="msg-pres-offline">last seen {{ timeAgo(c.other_last_seen) }}</span>
              </div>
            </div>

            <div v-if="c.unread_count > 0" class="msg-badge">{{ c.unread_count }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
html[data-theme="light"] body {
  background: #EBE7E1 !important;
  color: #1A1A1A !important;
}

html[data-theme="dark"] body {
  background: #0A0A0A !important;
  color: #F5F0E8 !important;
}

html[data-theme="light"] .msg-root,
html[data-theme="light"] .msg-layout-wrapper,
html[data-theme="light"] .msg-root #layout-wrapper,
html[data-theme="light"] .msg-root .msg-container {
  background: #EBE7E1 !important;
  color: #1A1A1A !important;
}

html[data-theme="dark"] .msg-root,
html[data-theme="dark"] .msg-layout-wrapper,
html[data-theme="dark"] .msg-root #layout-wrapper,
html[data-theme="dark"] .msg-root .msg-container {
  background: #0A0A0A !important;
  color: #F5F0E8 !important;
}
</style>

<style>
:root {
  --fm-h: #F5F0E8;
  --fm-s: #6B6356;
  --fm-m: #A09888;
  --fm-bg1: #141414;
  --fm-bg2: #0F0F0F;
  --fm-bg3: #1E1E1E;
  --fm-br: #1E1E1E;
}

html[data-theme="light"] {
  --fm-h: #1A1A1A;
  --fm-s: #777;
  --fm-m: #555;
  --fm-bg1: #FFFFFF;
  --fm-bg2: #EBE7E1;
  --fm-bg3: #E9E4DC;
  --fm-br: #E2DCD3;
}
</style>

<style scoped>
.msg-root,
.msg-layout-wrapper {
  background: var(--fm-bg2, #0F0F0F) !important;
  min-height: 100vh;
  color: var(--fm-h, #F5F0E8);
}

.msg-topbar {
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 12px 0;
  background: var(--fm-bg1, #141414) !important;
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--fm-br, #1E1E1E);
}

.msg-title {
  color: var(--fm-h, #F5F0E8);
  font-weight: 700;
  font-size: 18px;
}

.msg-back {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: var(--fm-bg2, #0F0F0F);
  color: var(--fm-h, #F5F0E8);
  font-size: 19px;
}

.msg-container {
  padding-top: 80px;
  padding-bottom: 60px;
  max-width: 700px;
}

.msg-search {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--fm-bg1, #141414);
  border: 1px solid var(--fm-br, #1E1E1E);
  border-radius: 22px;
  padding: 9px 16px;
  margin-bottom: 14px;
}

.msg-search i {
  color: var(--fm-s, #6B6356);
  font-size: 19px;
}

.msg-search input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: var(--fm-h, #F5F0E8);
  font-size: 14px;
}

.msg-conv {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: var(--fm-bg1, #141414);
  border: 1px solid var(--fm-br, #1E1E1E);
  border-radius: 12px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all .2s;
}

.msg-conv:hover {
  border-color: #BFA164;
  transform: translateX(2px);
}

.msg-avatar-wrap {
  position: relative;
  flex-shrink: 0;
}

.msg-online-dot {
  position: absolute;
  bottom: 1px;
  right: 1px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #50C878;
  border: 2px solid var(--fm-bg2, #0A0A0A);
}

.msg-unread-dot {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #50C878;
  border: 2px solid var(--fm-bg1, #141414);
}

.msg-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--fm-m, #A09888);
}

.msg-name.unread {
  color: var(--fm-h, #F5F0E8);
}

.msg-time {
  font-size: 11px;
  color: var(--fm-s, #777);
  white-space: nowrap;
}

.msg-preview {
  font-size: 12px;
  color: var(--fm-s, #6B6356);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.msg-preview.unread {
  color: var(--fm-m, #A09888);
  font-weight: 500;
}

.msg-context {
  font-size: 10px;
  color: var(--fm-s, #777);
  margin-top: 4px;
}

.msg-context i {
  font-size: 11px;
  margin-right: 2px;
}

.msg-badge {
  background: #BFA164;
  color: #0A0A0A;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 12px;
  flex-shrink: 0;
}

.msg-empty-icon {
  font-size: 56px;
  color: var(--fm-s, #6B6356);
}

.msg-empty-text {
  color: var(--fm-s, #6B6356);
}

.msg-empty-subtext {
  color: var(--fm-s, #777);
  font-size: 13px;
}

/* Presence pill — WhatsApp-style "Online" or "last seen X" line under each row */
.msg-presence{margin-top:3px;font-size:11px;line-height:1.2}
.msg-pres-online{color:#27AE60;font-weight:600;display:inline-flex;align-items:center;gap:5px}
.msg-pres-dot{width:8px;height:8px;border-radius:50%;background:#27AE60;display:inline-block;box-shadow:0 0 0 2px rgba(39,174,96,0.18)}
.msg-pres-offline{color:#7A6F60}

@media (max-width: 768px) {
  /* Topbar — tighter, hide Dashboard label (icon only via inserted span below) */
  .msg-topbar .container { padding: 6px 10px !important; }
  .msg-topbar .container > .d-flex > .gap-2 { gap: 6px !important; }
  .msg-back { width: 34px; height: 34px; font-size: 18px; }
  .msg-title { font-size: 14px; }
  .msg-topbar img[alt="Flockr"] { height: 26px !important; }
  .msg-topbar .btn-outline-warning { padding: 4px 8px !important; font-size: 11px; }

  /* Page padding + safe-area for bottom nav */
  .msg-container {
    padding: 14px 10px calc(70px + env(safe-area-inset-bottom, 0px)) !important;
  }

  /* Search row */
  .msg-search { padding: 8px 12px !important; border-radius: 22px; }
  .msg-search i { font-size: 18px; }
  .msg-search input { font-size: 13px; }

  /* Conversation rows */
  .msg-conv {
    padding: 10px 12px !important;
    gap: 10px !important;
    border-radius: 12px;
    margin-bottom: 6px;
  }
  .msg-avatar-wrap > * { width: 40px !important; height: 40px !important; }
  .msg-name { font-size: 13px; }
  .msg-time { font-size: 10px; flex-shrink: 0; padding-left: 6px; }
  .msg-preview { font-size: 12px; line-height: 1.35; }
  .msg-context { font-size: 10px; margin-top: 3px; }
  .msg-context i { font-size: 12px; }
  .msg-badge {
    font-size: 10px;
    padding: 2px 7px;
    margin-left: 4px;
    flex-shrink: 0;
  }
  .msg-unread-dot, .msg-online-dot {
    width: 9px !important; height: 9px !important;
  }
  .msg-empty-icon { font-size: 48px !important; }
  .msg-empty-text { font-size: 14px !important; }
  .msg-empty-subtext { font-size: 12px !important; padding: 0 16px; }
}

/* Extra-small phones */
@media (max-width: 380px) {
  .msg-title { display: none; }
  .msg-topbar .btn-outline-warning { font-size: 0; padding: 6px 8px !important; }
  .msg-topbar .btn-outline-warning::before {
    content: "\F00DE"; font-family: "Material Design Icons";
    font-size: 16px;
  }
}
</style>

<script>
/* eslint-disable */
import { execute, flocker, uploads } from "@/api";
import UserAvatar from "@/components/flockr-users/UserAvatar.vue";

export default {
  name: "MessagesInbox",
  components: { UserAvatar },
  data() {
    return {
      darkMode: (document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || 'dark') !== 'light',
      themeObserver: null,
      loading: true,
      conversations: [],
      search: ''
    };
  },
  computed: {
    filteredConversations() {
      var q = (this.search || '').trim().toLowerCase();
      if (!q) return this.conversations;
      return this.conversations.filter(function(c) {
        return ((c.other_name || '').toLowerCase().indexOf(q) > -1) ||
               ((c.last_message_preview || '').toLowerCase().indexOf(q) > -1);
      });
    }
  },
  mounted() {
    this.syncTheme();
    this.themeObserver = new MutationObserver(this.syncTheme);
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
    this.load();
  },
  beforeUnmount() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  },
  methods: {
    syncTheme() {
      var t = document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || 'dark';
      window.__flockrSetTheme(t);
      this.darkMode = t !== 'light';

      if (document.body) {
        document.body.setAttribute('data-sidebar', this.darkMode ? 'dark' : 'light');
        document.body.style.background = this.darkMode ? '#0A0A0A' : '#EBE7E1';
        document.body.style.color = this.darkMode ? '#F5F0E8' : '#1A1A1A';
      }

      var el = this.$el;
      if (el) {
        el.style.background = this.darkMode ? '#0A0A0A' : '#EBE7E1';
        el.style.color = this.darkMode ? '#F5F0E8' : '#1A1A1A';
      }
    },

    goBack() {
      if (window.history.length > 1) this.$router.go(-1);
      else this.$router.push('/dashboard');
    },
    isOnline(c){
      var raw = c && c.other_online;
      if (raw === 1 || raw === '1' || raw === true || raw === 'true') return true;
      // Fallback heuristic: treat as online if last_seen_at is within 5 minutes
      if (c && c.other_last_seen) {
        var t = new Date(String(c.other_last_seen).replace(' ','T')).getTime();
        if (!isNaN(t) && (Date.now() - t) < 5 * 60 * 1000) return true;
      }
      return false;
    },
    openChat(c) {
      var me = sessionStorage.getItem('userId');
      var otherId = (c.participant_one_id === me) ? c.participant_two_id : c.participant_one_id;
      window.dispatchEvent(new CustomEvent('flockr-open-chat', { detail: {
        conversationId: c.id, userId: otherId, name: c.other_name || 'Chat', avatar: c.other_avatar || ''
      }}));
      // reflect read locally
      if (me === c.participant_one_id) c.participant_one_unread = 0; else c.participant_two_unread = 0;
      c.unread_count = 0;
    },
    pu(p) {
      if (!p) return '';
      if (p.startsWith && p.startsWith('http')) return p;
      return uploads + String(p).replace('uploads/', '');
    },

    initials(n) {
      if (!n) return '?';
      return n.split(' ').map(function(w) { return w.charAt(0); }).join('').substring(0, 2).toUpperCase();
    },

    timeAgo(d) {
      if (!d) return '';
      var s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
      if (s < 60) return 'now';
      if (s < 3600) return Math.floor(s / 60) + 'm';
      if (s < 86400) return Math.floor(s / 3600) + 'h';
      if (s < 604800) return Math.floor(s / 86400) + 'd';
      return new Date(d).toLocaleDateString('en-KE', { day: 'numeric', month: 'short' });
    },

    formatPreview(p) {
      if (!p) return 'No messages yet';
      var trimmed = p.trim();

      if (trimmed.indexOf('"_type":"listing_card"') > -1 || trimmed.indexOf('"_type\\":"listing_card') > -1) {
        var m = trimmed.match(/"title"\s*:\s*"([^"]+)"/);
        var title = m ? m[1] : 'Listing';
        return '\uD83D\uDCE6 ' + title + ' — Interested in this listing';
      }

      if (trimmed.indexOf('"_type":"location"') > -1 || trimmed.indexOf('"_type\\":"location') > -1) {
        return '\uD83D\uDCCD Shared location';
      }

      if (trimmed.charAt(0) === '{') {
        try {
          var d = JSON.parse(trimmed);
          if (d._type === 'listing_card') return '\uD83D\uDCE6 ' + (d.title || 'Listing') + ' — Interested in this listing';
          if (d._type === 'location') return '\uD83D\uDCCD Shared location';
          return d.text || 'Message';
        } catch (e) {
          var mt = trimmed.match(/"title"\s*:\s*"([^"]+)"/);
          if (mt) return '\uD83D\uDCE6 ' + mt[1];
          return 'Sent an attachment';
        }
      }

      if (trimmed.match(/^\d+:\d+$/)) return '\uD83C\uDFA4 Voice note';
      return p;
    },

    async load() {
      var userId = sessionStorage.getItem('userId');
      if (!userId) {
        this.$router.push('/login');
        return;
      }

      try {
        var cached = sessionStorage.getItem('inbox_conversations');
        if (cached) {
          this.conversations = JSON.parse(cached);
          this.loading = false;
        }
      } catch (e) {}

      try {
        var f = new FormData();
        f.append('function', 'getConversations');
        f.append('userId', userId);
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          this.conversations = r.data.data.conversations || [];
          try {
            sessionStorage.setItem('inbox_conversations', JSON.stringify(this.conversations));
          } catch (e) {}
        }
      } catch (e) {
        console.error(e);
      }

      this.loading = false;
    }
  }
};
</script>
